//Unit test for this file
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useDeleteContactMutation, useGetContactListQuery } from '../../../app/services/account';
import theme from '../../../app/theme';
import { useToast } from '../../../app/toast';
import CustomPagination from '../../../common/components/CustomPagination';
import DataList from '../../../common/components/DataList';
import Loading from '../../../common/components/Loading';
import PageHeader from '../../../common/components/PageHeader';
import PopupModal from '../../../common/components/PopupModal';
import RoundedContainer from '../../../common/components/RoundedContainer';
import DeactivatePatientIcon from '../../../common/components/icons/DeactivatePatientIcon';
import EditUserIcon from '../../../common/components/icons/EditInterviewIcon';
import DashboardLayout from '../../../common/layouts/Dashboard';
import {
  selectContentPopup,
  selectIsOpenPopup,
  setContentPopup,
  setIsOpenPopup,
} from '../../../common/slices/globalSlice';
import { Status } from '../../../common/utils/commonUtils';
import { getPageTitle } from '../../../common/utils/pageUtils';
import { canViewBySuperAdmin, isClinicAdmin } from '../../../common/utils/permissionUtils';
import { SortDirs, SortFields } from '../../../common/utils/sortInfo';
import { UserStatus } from '../../../common/utils/userListUtils';
import { ContactsItem } from '../../../types/ContactType';
import { PageProps } from '../../../types/PageProps';
import { PopupContent } from '../../../types/PopupType';
import { selectUser } from '../../auth/authSlice';
import ContextMenu from '../../patient/ContextMenu';
import PatientSortMenu from '../../patient/PatientSortMenu';

const StyledAddWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  margin: '12px 0px',
  width: '100%',
  padding: '0px 8px',
  columnGap: '8px',
  [theme.breakpoints.up('md')]: {
    marginTop: 0,
    width: 'auto',
    flexBasis: '40%',
    maxWidth: '1200px',
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '16px',
  },
}));

const StyledSearchBox = styled(OutlinedInput)(({ theme }) => ({
  borderRadius: '8px',
  backgroundColor: theme.xPalette.white,
  minWidth: '240px',
  [theme.breakpoints.up('md')]: {
    flexBasis: '65%',
  },
  [theme.breakpoints.down('md')]: {
    flexBasis: 'unset',
    width: '74%',
    minWidth: 'unset',
  },
}));

const StyledAddUserBox = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  lineHeight: 0,
  minWidth: '140px',
  ml: 1,
  [theme.breakpoints.up('md')]: {
    flexBasis: '23%',
  },
  [theme.breakpoints.down('md')]: {
    width: '96%',
    height: '56px',
    ml: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.xPalette.lightGrey,
    fontWeight: theme.typography.h1.fontWeight,
    backgroundColor: theme.xPalette.background,
    borderBottom: 0,
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    fontWeight: theme.typography.h4.fontWeight,
  },
}));

const StyledSortIconBox = styled(Box)(({ theme }) => ({
  display: 'inline',
  color: theme.palette.primary.main,
  verticalAlign: 'middle',
}));

const defaultPage = 1;

export default function ContactsPage({ breadcrumbs }: PageProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const searchTextboxRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clinicalAccountId } = useParams();
  const [pageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [contactIdAction, setContactIdAction] = useState<number>(1);
  const toast = useToast();
  const { state } = useLocation();
  const [newNavBackUrl, setNewNavBackUrl] = useState<string>(
    `/dashboard/accounts/${clinicalAccountId}/contacts`
  );
  const { navBackUrl } = !!state && state;
  const [displayContactData, setDisplayContactData] = useState<ContactsItem[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isOpenPopup = useAppSelector(selectIsOpenPopup);
  const contentPopup = useAppSelector(selectContentPopup);
  const currentUser = useAppSelector(selectUser);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const tableHeaders: string[] = Object.values(
    t('account.contacts.manageContacts.tableHeader', {
      returnObjects: true,
    })
  );

  const deleteContactContentPopup = {
    title: t('account.contacts.manageContacts.deleteContact.title'),
    description: t('account.contacts.manageContacts.deleteContact.description'),
    btnOk: t('account.contacts.manageContacts.deleteContact.btnOk'),
    btnClose: t('account.contacts.manageContacts.deleteContact.btnClose'),
    toastMessage: t('account.contacts.manageContacts.deleteContact.toastMessage'),
  } as PopupContent;

  const searchOptions = {
    page: parseInt(searchParams.get('page') || '1'),
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || UserStatus.ACTIVE,
    sortField: searchParams.get('sort') || SortFields.Name,
    sortDir: searchParams.get('dir') || SortDirs.Asc,
  };

  const currentPage = searchOptions.page;

  const {
    data: contactData,
    isLoading: isLoadingContact,
    refetch: refetchContactList,
  } = useGetContactListQuery({
    clinicAccountId: clinicalAccountId,
    search: searchOptions.search,
    page: currentPage,
    pageSize: pageSize,
    status: searchOptions.status,
    sort: `${searchOptions.sortField} ${searchOptions.sortDir}`,
  });

  const [deleteContact] = useDeleteContactMutation();

  useEffect(() => {
    if (contactData) {
      setDisplayContactData(contactData.items);
      setTotalPages(Math.ceil(contactData.total / pageSize));
    }
  }, [dispatch, pageSize, contactData]);

  const updateUrl = (page: number, search: string | undefined, sort: string, dir: string) => {
    const updatedUrl = `/dashboard/accounts/${clinicalAccountId}/contacts?page=${page}&search=${encodeURIComponent(
      search || ''
    )}&sort=${sort}&dir=${dir}`;
    navigate(updatedUrl);
    setNewNavBackUrl(updatedUrl);
  };

  const handleSearch = () => {
    const searchInput = searchTextboxRef.current?.value || '';
    updateUrl(defaultPage, searchInput, searchOptions.sortField, searchOptions.sortDir);
  };

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (value: number) => {
    updateUrl(value, searchOptions.search, searchOptions.sortField, searchOptions.sortDir);
  };

  const handleMenuSorting = (sortField: string, sortDir: string) => {
    updateUrl(defaultPage, searchOptions.search, sortField, sortDir);
  };

  const onHeaderClicked = (header?: string) => {
    if (header) {
      const sortHeader = header === 'site(s)' ? header.slice(0, 4) : header;
      updateUrl(
        defaultPage,
        searchOptions.search,
        sortHeader,
        sortHeader !== searchOptions.sortField || searchOptions.sortDir === SortDirs.Desc
          ? SortDirs.Asc
          : SortDirs.Desc
      );
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showDeleteContactPopup = async (contactId: number) => {
    dispatch(setContentPopup({ content: deleteContactContentPopup }));
    dispatch(setIsOpenPopup({ value: true }));
    setContactIdAction(contactId);
    handleClose();
  };

  const handleDeleteContact = async (successCallback?: () => void) => {
    try {
      await deleteContact({
        id: contactIdAction,
      }).unwrap();
      if (successCallback) {
        successCallback();
      }
      toast.publish(t('account.contacts.manageContacts.deleteContact.success'), 'success');
      refetchContactList();
      updateUrl(defaultPage, searchOptions.search, searchOptions.sortField, searchOptions.sortDir);
    } catch (e) {
      toast.publish(t('account.contacts.manageContacts.deleteContact.fail'), 'error');
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const renderContextMenu = (item: ContactsItem) => {
    const menuItems = [
      {
        id: `edit-contact-${item.id}`,
        isDisplayed: canViewBySuperAdmin(currentUser.roleId) || isClinicAdmin(currentUser.roleId),
        label: t('account.contacts.manageContacts.menuContext.editContact'),
        icon: <EditUserIcon />,
        onClick: () => {
          navigate(`/dashboard/accounts/${clinicalAccountId}/contacts/edit-contact/${item.id}`, {
            state: { newNavBackUrl },
          });
        },
      },
      {
        id: `delete-contact-${item.id}`,
        isDisplayed: canViewBySuperAdmin(currentUser.roleId) || isClinicAdmin(currentUser.roleId),
        label: t('account.contacts.manageContacts.menuContext.deleteContact'),
        icon: <DeactivatePatientIcon />,
        onClick: () => showDeleteContactPopup(item.id),
      },
    ];

    const checkShowContextMenu = menuItems.some((item) => item.isDisplayed);
    return checkShowContextMenu ? (
      <ContextMenu
        id={`contact-${item.id}`}
        anchorEl={anchorEl}
        handleClick={handleClick}
        handleClose={handleClose}
        menuItems={menuItems}
      />
    ) : (
      <></>
    );
  };

  const renderDesktopData = (data: ContactsItem[]) => {
    return (
      <TableContainer component={Paper} elevation={0} sx={{ mt: 2, borderRadius: '16px 16px 0 0' }}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header, index) => {
                const eventHeaders = ['name'];
                const checkSortEvent =
                  searchOptions.sortDir === SortDirs.Asc &&
                  (searchOptions.sortField === header.toLowerCase() ||
                    searchOptions.sortField === header.slice(0, 4).toLowerCase());

                const sortIcon = eventHeaders.includes(header.toLowerCase()) ? (
                  <StyledSortIconBox>
                    {checkSortEvent ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </StyledSortIconBox>
                ) : (
                  <></>
                );
                return (
                  <StyledTableCell
                    key={`${header} ${index}`}
                    onClick={
                      eventHeaders.includes(header.toLowerCase())
                        ? () => onHeaderClicked(header.toLowerCase())
                        : undefined
                    }
                  >
                    {header} {sortIcon}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item: ContactsItem) => {
              return (
                <TableRow key={item.id}>
                  <StyledTableCell sx={{ maxWidth: '500px', minWidth: '150px' }}>
                    <Link
                      sx={{ fontSize: '1em', textAlign: 'left' }}
                      component="button"
                      onClick={() => {
                        navigate(
                          `/dashboard/accounts/${clinicalAccountId}/contacts/edit-contact/${item.id}`,
                          {
                            state: { newNavBackUrl },
                          }
                        );
                      }}
                    >
                      {`${item.firstName} ${item.lastName}`}
                    </Link>
                  </StyledTableCell>
                  <StyledTableCell sx={{ wordBreak: 'break-all', minWidth: '300px' }}>
                    {item.email}
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: '190px' }}>{item.phone}</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: '100px' }}>{item.role}</StyledTableCell>
                  <StyledTableCell align="center">{renderContextMenu(item)}</StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderMobileData = (data: ContactsItem[]) => {
    return (
      <DataList
        lineHeight={'auto'}
        data={data.map((item: ContactsItem) => {
          const commonCondition = item.status !== Status.DEACTIVATED;
          const listItems = [
            [
              <>{t('account.contacts.manageContacts.tableHeader.email')}</>,
              <Box sx={{ wordBreak: 'break-word' }}>{item.email}</Box>,
            ],
            [<>{t('account.contacts.manageContacts.tableHeader.role')}</>, <>{item.role}</>],
          ];

          return {
            leftHeader:
              commonCondition && canViewBySuperAdmin(currentUser.roleId) ? (
                <Link
                  sx={{ fontSize: '20px', textAlign: 'left', fontWeight: '600' }}
                  component="button"
                  onClick={() => {
                    navigate(
                      `/dashboard/account/${clinicalAccountId}/contacts/edit-contact/${item.id}`,
                      {
                        state: { newNavBackUrl },
                      }
                    );
                  }}
                >
                  {`${item.firstName} ${item.lastName}`}
                </Link>
              ) : (
                <Typography variant="body1" fontWeight="bold" fontSize={20}>
                  {`${item.firstName} ${item.lastName}`}
                </Typography>
              ),
            rightHeader: renderContextMenu(item),
            items: [...listItems],
          };
        })}
        customStyle={{
          borderRadius: '16px 16px 0 0',
        }}
      />
    );
  };

  if (breadcrumbs?.length && isClinicAdmin(currentUser.roleId)) {
    breadcrumbs = [breadcrumbs[0], breadcrumbs[2]];
  }

  return (
    <DashboardLayout breadcrumbs={breadcrumbs}>
      <Helmet>
        <title>{getPageTitle(t('account.contacts.title'))}</title>
      </Helmet>
      <Container maxWidth="xl" disableGutters={!isDesktop}>
        <Box
          sx={{
            [theme.breakpoints.up('md')]: {
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
          }}
        >
          <PageHeader
            sx={{ mb: 2, mx: isDesktop ? 0 : 2 }}
            headerText={t('account.contacts.title')}
            backUrl={navBackUrl}
          />

          <StyledAddWrapper>
            <StyledSearchBox
              name="contact-search-field"
              inputRef={searchTextboxRef}
              onKeyUp={handleSearchKeyUp}
              defaultValue={searchOptions.search}
              placeholder={t('account.contacts.searchContacts')}
              endAdornment={
                <IconButton type="submit" aria-label="search" onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              }
            />
            {!isDesktop && (
              <PatientSortMenu
                sx={{
                  width: '20%',
                  flexShrink: 0,
                  borderRadius: '8px',
                  minWidth: 'unset',
                }}
                sortField={searchOptions.sortField}
                sortDir={searchOptions.sortDir}
                onSorted={handleMenuSorting}
                isUser={true}
              />
            )}
            <StyledAddUserBox
              type="button"
              fullWidth
              variant="contained"
              onClick={() => {
                navigate(`/dashboard/accounts/${clinicalAccountId}/contacts/add-contact`, {
                  state: { newNavBackUrl },
                });
              }}
            >
              {t('account.contacts.addContact')}
            </StyledAddUserBox>
          </StyledAddWrapper>
        </Box>

        {isLoadingContact ? (
          <Loading />
        ) : (
          <Box ref={tableRef}>
            {displayContactData?.length === 0 ? (
              <RoundedContainer sx={{ mt: 2, p: 2 }}>
                <Typography>{t('user.manageUsers.error.usersNotFound')}</Typography>
              </RoundedContainer>
            ) : (
              <>
                {isDesktop
                  ? renderDesktopData(displayContactData)
                  : renderMobileData(displayContactData)}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 4,
                    borderBottomLeftRadius: '15px',
                    borderBottomRightRadius: '15px',
                    background: theme.xPalette.white,
                    mt: 0,
                    [theme.breakpoints.down('sm')]: {
                      mt: 1,
                      py: 2,
                    },
                  }}
                >
                  <CustomPagination
                    changePage={(e, value) => handlePageChange(value)}
                    currentPage={searchOptions.page}
                    pages={totalPages}
                    pageRef={tableRef}
                  />
                </Box>
              </>
            )}
          </Box>
        )}
        <PopupModal
          isOpen={isOpenPopup}
          contentPopup={contentPopup}
          onClick={handleDeleteContact}
        />
      </Container>
    </DashboardLayout>
  );
}
